import {createSelector} from '@reduxjs/toolkit';
import {RootState} from '../../rootReducer';
import {getDefaultBuildingId} from '../buildings';
import {getIsNew} from '../user';

const selectPreferencesState = (state: RootState) => state.data.preferences;

export const getPreferences = createSelector(
  selectPreferencesState,
  (preferencesState) => preferencesState.preferences,
);

export const getIsPreferencesInitialized = createSelector(
  selectPreferencesState,
  (preferencesState) => preferencesState.isInitialized,
);

export const getIsLoadingPreferences = createSelector(
  selectPreferencesState,
  (preferencesState) => preferencesState.isLoading,
);

export const getIsSavingPreferences = createSelector(
  selectPreferencesState,
  (preferencesState) => preferencesState.isSaving,
);

export const getPreferencesError = createSelector(selectPreferencesState, (preferencesState) => preferencesState.error);

export const getIsAutomaticDeskBookingEnabled = createSelector(
  getPreferences,
  (preferences) =>
    // If preference is on building level (i.e. no floor preference is set), it should not be enabled
    !!preferences.workspaceLocation?.floorId && !!preferences.workspaceLocation?.isDeskRequired,
);

export const getPreferredNodeId = createSelector(
  getDefaultBuildingId,
  getPreferences,
  (defaultBuildingId, preferences) =>
    preferences.workspaceLocation?.areaId || preferences.workspaceLocation?.floorId || defaultBuildingId,
);

export const getIsOnboarded = createSelector(
  getIsNew,
  getPreferences,
  // can not use this selector because: Error: createSelector expects all input-selectors to be functions, but received the following types: [undefined, function selector2(), function wrappedSelector(), function memoized(), function memoized(), function memoized(), function memoized()]
  (_state: RootState, isParkingFeatureVisible: boolean) => isParkingFeatureVisible,
  (isNew, preferences, isParkingFeatureVisible) => {
    // User should return to the onboarding when either of the preferences are missing (i.e. a change in default building could reset preferences)
    return (
      !isNew &&
      preferences.workspaceLocation !== null &&
      (isParkingFeatureVisible ? preferences.parkingLocation !== null : true)
    );
  },
);

export const getIsThreeDMapsPreferred = createSelector(
  selectPreferencesState,
  (preferencesState) => preferencesState.preferences.threeDMapsPreferred,
);

// Parking
const NO_LICENCE_PLATES = Array<string>();

export const getUserDismissedParkingSuggestionPopUp = createSelector(
  getPreferences,
  (preferences) => preferences.userDismissedParkingSuggestionPopUp,
);

export const getUserDismissedParkingIntroduction = createSelector(
  getPreferences,
  (preferences) => preferences.userDismissedParkingIntroduction,
);

export const getLicensePlate = createSelector(getPreferences, (preferences) => preferences.licensePlate ?? '');

export const getLicensePlates = createSelector(
  getPreferences,
  (preferences) => preferences.licensePlates ?? NO_LICENCE_PLATES,
);

export const getIsUpdatingParkingPreferences = createSelector(
  selectPreferencesState,
  (preferencesState) => preferencesState.isSaving,
);
